import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sd } from '@sd/lib/sdPaths';
import { Header } from '@/components/layout/Header';
import { Sidebar } from '@/components/layout/Sidebar';
import { CommandPalette } from '@/components/CommandPalette';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { useAgentCompactLayout } from '@sd/hooks/use-mobile';
import { 
  Search,
  BookOpen,
  FileText,
  Folder,
  Star,
  Clock,
  ChevronRight,
  Plus,
  Eye,
  ThumbsUp,
  Menu,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

interface Article {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  views: number;
  helpful: number;
  updatedAt: string;
  isFeatured?: boolean;
}

interface Category {
  id: string;
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  count: number;
}

const categories: Category[] = [
  { id: 'all', name: 'All Articles', icon: BookOpen, count: 156 },
  { id: 'getting-started', name: 'Getting Started', icon: Folder, count: 12 },
  { id: 'troubleshooting', name: 'Troubleshooting', icon: Folder, count: 45 },
  { id: 'how-to', name: 'How-To Guides', icon: Folder, count: 38 },
  { id: 'policies', name: 'IT Policies', icon: Folder, count: 15 },
  { id: 'security', name: 'Security', icon: Folder, count: 28 },
  { id: 'software', name: 'Software', icon: Folder, count: 18 },
];

const articles: Article[] = [
  {
    id: 'kb-001',
    title: 'How to Reset Your Password',
    excerpt: 'Step-by-step guide to reset your company account password securely.',
    category: 'getting-started',
    views: 2450,
    helpful: 189,
    updatedAt: '2 days ago',
    isFeatured: true,
  },
  {
    id: 'kb-002',
    title: 'VPN Connection Troubleshooting',
    excerpt: 'Common solutions for VPN connectivity issues on Windows and macOS.',
    category: 'troubleshooting',
    views: 1823,
    helpful: 145,
    updatedAt: '1 week ago',
    isFeatured: true,
  },
  {
    id: 'kb-003',
    title: 'Setting Up Two-Factor Authentication',
    excerpt: 'Protect your account with 2FA using Microsoft Authenticator.',
    category: 'security',
    views: 1567,
    helpful: 132,
    updatedAt: '3 days ago',
    isFeatured: true,
  },
  {
    id: 'kb-004',
    title: 'Email Signature Setup Guide',
    excerpt: 'Create and configure your company email signature in Outlook.',
    category: 'how-to',
    views: 890,
    helpful: 78,
    updatedAt: '5 days ago',
  },
  {
    id: 'kb-005',
    title: 'Remote Desktop Connection Guide',
    excerpt: 'How to connect to your office computer from home.',
    category: 'how-to',
    views: 756,
    helpful: 65,
    updatedAt: '1 week ago',
  },
  {
    id: 'kb-006',
    title: 'Software Installation Request Process',
    excerpt: 'How to request new software installation through IT.',
    category: 'policies',
    views: 654,
    helpful: 54,
    updatedAt: '2 weeks ago',
  },
  {
    id: 'kb-007',
    title: 'Outlook Not Syncing - Fix Guide',
    excerpt: 'Troubleshoot email sync issues in Microsoft Outlook.',
    category: 'troubleshooting',
    views: 1234,
    helpful: 98,
    updatedAt: '4 days ago',
  },
  {
    id: 'kb-008',
    title: 'Installing Microsoft Office 365',
    excerpt: 'Download and install Office 365 on your work computer.',
    category: 'software',
    views: 567,
    helpful: 45,
    updatedAt: '1 week ago',
  },
];

const KnowledgeBase = () => {
  const navigate = useNavigate();
  const isCompact = useAgentCompactLayout();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);

  const filteredArticles = articles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || article.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const featuredArticles = articles.filter(a => a.isFeatured);

  const CategoryNav = ({ onSelect }: { onSelect?: () => void }) => (
    <nav className="p-2 space-y-1">
      {categories.map((category) => {
        const Icon = category.icon;
        return (
          <button
            key={category.id}
            onClick={() => {
              setSelectedCategory(category.id);
              setSelectedArticle(null);
              onSelect?.();
            }}
            className={cn(
              "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
              selectedCategory === category.id
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-muted hover:text-foreground"
            )}
          >
            <Icon className="h-4 w-4 shrink-0" />
            <span className="flex-1 text-left truncate">{category.name}</span>
            <span className="text-xs opacity-60 shrink-0">{category.count}</span>
          </button>
        );
      })}
    </nav>
  );

  return (
    <div className="h-screen flex flex-col bg-background overflow-hidden">
      <Header onSearchClick={() => setCommandPaletteOpen(true)} />
      
      <CommandPalette 
        open={commandPaletteOpen}
        onOpenChange={setCommandPaletteOpen}
        onCreateTicket={() => navigate(sd('tickets/new'))} 
        onSelectTicket={() => {}}
      />
      
      <div className="flex-1 flex overflow-hidden">
        {/* Main nav sidebar — Sheet on mobile */}
        {isCompact ? (
          <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
            <SheetContent side="left" className="w-64 p-0">
              <Sidebar
                collapsed={false}
                onToggle={() => setSidebarOpen(false)}
                activeQueue="all"
                onQueueChange={() => {}}
                activeTab="tickets"
                onTabChange={(tab) => {
                  setSidebarOpen(false);
                  if (tab === 'tickets') navigate(sd('tickets'));
                  if (tab === 'assets') navigate(sd('assets'));
                }}
              />
            </SheetContent>
          </Sheet>
        ) : (
          <Sidebar
            collapsed={sidebarCollapsed}
            onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
            activeQueue="all"
            onQueueChange={() => {}}
            activeTab="tickets"
            onTabChange={(tab) => {
              if (tab === 'tickets') navigate(sd('tickets'));
              if (tab === 'assets') navigate(sd('assets'));
            }}
          />
        )}

        {/* KB Categories Panel — Sheet on mobile, fixed aside on desktop */}
        {isCompact ? (
          <Sheet open={categoriesOpen} onOpenChange={setCategoriesOpen}>
            <SheetContent side="left" className="w-64 p-0 flex flex-col">
              <div className="p-4 border-b border-border flex items-center justify-between">
                <h1 className="text-lg font-semibold text-foreground">Knowledge Base</h1>
                <div className="flex items-center gap-1">
                  <Button size="icon" variant="ghost" className="h-8 w-8">
                    <Plus className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setCategoriesOpen(false)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <ScrollArea className="flex-1">
                <CategoryNav onSelect={() => setCategoriesOpen(false)} />
              </ScrollArea>
            </SheetContent>
          </Sheet>
        ) : (
          <aside className="w-56 shrink-0 border-r border-border flex flex-col lg:w-64">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h1 className="text-base font-semibold text-foreground lg:text-lg">Knowledge Base</h1>
              <Button size="icon" variant="ghost" className="h-8 w-8">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <ScrollArea className="flex-1">
              <CategoryNav />
            </ScrollArea>
          </aside>
        )}

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Mobile toolbar — only visible on compact */}
          {isCompact && (
            <div className="flex items-center gap-2 border-b border-border px-3 py-2 shrink-0">
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setSidebarOpen(true)}>
                <Menu className="h-4 w-4" />
              </Button>
              <button
                onClick={() => setCategoriesOpen(true)}
                className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground"
              >
                <BookOpen className="h-4 w-4" />
                <span>{categories.find(c => c.id === selectedCategory)?.name ?? 'Categories'}</span>
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
              {selectedArticle && (
                <button
                  onClick={() => setSelectedArticle(null)}
                  className="ml-auto flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                >
                  <ChevronRight className="h-3.5 w-3.5 rotate-180" />
                  Back
                </button>
              )}
            </div>
          )}

          {!selectedArticle ? (
            <ScrollArea className="flex-1">
              <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8">
                {/* Search */}
                <div className="mb-5 sm:mb-8">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground sm:left-4 sm:h-5 sm:w-5" />
                    <Input
                      placeholder="Search articles..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 h-10 sm:pl-12 sm:h-12 sm:text-base bg-muted"
                    />
                  </div>
                </div>

                {/* Featured Articles */}
                {selectedCategory === 'all' && !searchQuery && (
                  <div className="mb-6 sm:mb-8">
                    <div className="flex items-center gap-2 mb-3 sm:mb-4">
                      <Star className="h-4 w-4 text-warning sm:h-5 sm:w-5" />
                      <h2 className="text-base font-semibold text-foreground sm:text-lg">Featured Articles</h2>
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 sm:gap-4">
                      {featuredArticles.map((article) => (
                        <button
                          key={article.id}
                          onClick={() => setSelectedArticle(article)}
                          className="p-3 rounded-lg border border-border bg-card hover:bg-muted/50 transition-colors text-left sm:p-4"
                        >
                          <h3 className="text-sm font-medium text-foreground mb-1 line-clamp-2 sm:mb-2">{article.title}</h3>
                          <p className="text-xs text-muted-foreground line-clamp-2">{article.excerpt}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* All Articles */}
                <div>
                  <div className="flex items-center gap-2 mb-3 sm:mb-4">
                    <FileText className="h-4 w-4 text-muted-foreground sm:h-5 sm:w-5" />
                    <h2 className="text-base font-semibold text-foreground sm:text-lg">
                      {selectedCategory === 'all' ? 'All Articles' : categories.find(c => c.id === selectedCategory)?.name}
                    </h2>
                    <Badge variant="secondary" className="ml-1 sm:ml-2">{filteredArticles.length}</Badge>
                  </div>
                  
                  <div className="space-y-2">
                    {filteredArticles.map((article) => (
                      <button
                        key={article.id}
                        onClick={() => setSelectedArticle(article)}
                        className="w-full p-3 rounded-lg border border-border bg-card hover:bg-muted/50 transition-colors text-left flex items-center gap-3 sm:p-4 sm:gap-4"
                      >
                        <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 sm:h-10 sm:w-10">
                          <FileText className="h-4 w-4 text-primary sm:h-5 sm:w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-medium text-foreground mb-0.5 sm:mb-1 truncate">{article.title}</h3>
                          <p className="text-xs text-muted-foreground line-clamp-1">{article.excerpt}</p>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground shrink-0 sm:gap-4">
                          <span className="hidden items-center gap-1 sm:flex">
                            <Eye className="h-3.5 w-3.5" />
                            {article.views}
                          </span>
                          <span className="hidden items-center gap-1 sm:flex">
                            <ThumbsUp className="h-3.5 w-3.5" />
                            {article.helpful}
                          </span>
                          <ChevronRight className="h-4 w-4" />
                        </div>
                      </button>
                    ))}
                  </div>

                  {filteredArticles.length === 0 && (
                    <div className="text-center py-10 text-muted-foreground sm:py-12">
                      <BookOpen className="h-10 w-10 mx-auto mb-4 opacity-50 sm:h-12 sm:w-12" />
                      <p className="text-sm sm:text-base">No articles found matching your search.</p>
                    </div>
                  )}
                </div>
              </div>
            </ScrollArea>
          ) : (
            <ScrollArea className="flex-1">
              <div className="max-w-3xl mx-auto p-4 sm:p-6 lg:p-8">
                {!isCompact && (
                  <button
                    onClick={() => setSelectedArticle(null)}
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6"
                  >
                    <ChevronRight className="h-4 w-4 rotate-180" />
                    Back to articles
                  </button>
                )}

                <article className="space-y-5 sm:space-y-6">
                  <div>
                    <Badge variant="secondary" className="mb-3 capitalize sm:mb-4">{selectedArticle.category.replace('-', ' ')}</Badge>
                    <h1 className="text-xl font-bold text-foreground mb-2 sm:text-2xl">{selectedArticle.title}</h1>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground sm:gap-4">
                      <span className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        Updated {selectedArticle.updatedAt}
                      </span>
                      <span className="flex items-center gap-1">
                        <Eye className="h-4 w-4" />
                        {selectedArticle.views} views
                      </span>
                    </div>
                  </div>

                  <div className="prose prose-invert max-w-none">
                    <p className="text-muted-foreground text-sm sm:text-base">{selectedArticle.excerpt}</p>
                    
                    <h2 className="text-base font-semibold text-foreground mt-5 mb-2 sm:text-lg sm:mt-6 sm:mb-3">Overview</h2>
                    <p className="text-sm text-muted-foreground sm:text-base">
                      This article provides detailed instructions on {selectedArticle.title.toLowerCase()}. 
                      Follow the steps below carefully to complete the process successfully.
                    </p>

                    <h2 className="text-base font-semibold text-foreground mt-5 mb-2 sm:text-lg sm:mt-6 sm:mb-3">Prerequisites</h2>
                    <ul className="list-disc pl-5 text-sm text-muted-foreground space-y-2 sm:pl-6 sm:text-base">
                      <li>Active company email account</li>
                      <li>Access to your computer or mobile device</li>
                      <li>Stable internet connection</li>
                    </ul>

                    <h2 className="text-base font-semibold text-foreground mt-5 mb-2 sm:text-lg sm:mt-6 sm:mb-3">Step-by-Step Instructions</h2>
                    <ol className="list-decimal pl-5 text-sm text-muted-foreground space-y-2 sm:pl-6 sm:text-base sm:space-y-3">
                      <li>Open your web browser and navigate to the company portal.</li>
                      <li>Click on the "Sign In" button in the top-right corner.</li>
                      <li>Enter your credentials and follow the prompts.</li>
                      <li>Complete any additional verification steps if required.</li>
                      <li>Confirm the changes have been applied successfully.</li>
                    </ol>

                    <h2 className="text-base font-semibold text-foreground mt-5 mb-2 sm:text-lg sm:mt-6 sm:mb-3">Troubleshooting</h2>
                    <p className="text-sm text-muted-foreground sm:text-base">
                      If you encounter any issues, please contact the IT Help Desk at extension 1234 or 
                      submit a support ticket through the service portal.
                    </p>
                  </div>

                  <div className="border-t border-border pt-5 sm:pt-6">
                    <p className="text-sm text-muted-foreground mb-3">Was this article helpful?</p>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" className="gap-2">
                        <ThumbsUp className="h-4 w-4" />
                        Yes ({selectedArticle.helpful})
                      </Button>
                      <Button variant="outline" size="sm">
                        No
                      </Button>
                    </div>
                  </div>
                </article>
              </div>
            </ScrollArea>
          )}
        </div>
      </div>
    </div>
  );
};

export default KnowledgeBase;
